import {
  ContractFilenames,
  DocumentTemplate,
  Offer,
  OfferDbId,
  OpportunityCustomer,
  OpportunitySfId,
  OpportunityShareSearchResult,
  SavedOffer
} from 'app/types/types'
import { GetSFDataWithoutConfigParams, ShareOpportunityParams } from '../api-types'
import { apiRouteUtils } from './route-utils'
import {
  ApiMasterData,
  CreateGendocXmlRequest,
  GetContractPdfRequest,
  RecreateDocumentsRequest,
  SendContractPdfRequest,
  UpdateDiscountApprovalStatusResponse
} from 'app/types/backend-api'
import { axiosGet, axiosPost } from './axios-impl'
import { UserSettings } from 'app/types/tables'
import { getUUID } from '../../app/common/common-utils'
import { OpportunitiesAndContacts } from 'app/app/(client)/redux/features/customers/customer-opportunity-async'
import { DocumentGenerator } from 'app/types/documentGenerator'
import { GenericAbortSignal } from 'axios'

export interface RequestParameters {
  path: string
  data?: any
  timeout?: number
  url?: string
  contentType?: string
  cacheControl?: string
  signal?: GenericAbortSignal
}

export function getUITabId(key = 'TAB_ID') {
  let tabId = sessionStorage.getItem(key)
  if (tabId === null) {
    tabId = getUUID()
    sessionStorage.setItem(key, tabId)
  }
  return tabId
}

export const getSFDataWithoutConfig = async (data: GetSFDataWithoutConfigParams): Promise<OpportunityCustomer> => {
  const params = { path: apiRouteUtils.sfDataWithOutConfig.url, data }
  return axiosPost<{ data: OpportunityCustomer }>(params)
    .then(result => result.data)
    .catch((_error: Error) => ({}) as OpportunityCustomer)
}

export const getCustomerData = (): Promise<OpportunityCustomer[] | undefined> =>
  axiosGet<OpportunityCustomer[]>({ path: apiRouteUtils.customerData.url })

export const getOpportunitiesAndContacts = (): Promise<OpportunitiesAndContacts | undefined> =>
  axiosGet<OpportunitiesAndContacts>({ path: `${apiRouteUtils.customerData.url}?opportunities-and-contacts` })

export const getConfigData = (): Promise<ApiMasterData> =>
  axiosGet<ApiMasterData>({ path: apiRouteUtils.masterData.url })

export const getSFData = (opportunityId: OpportunitySfId): Promise<OpportunityCustomer> =>
  axiosGet<OpportunityCustomer>({ path: `${apiRouteUtils.opportunity.url}/${opportunityId}` })

export const getSearchOpportunityData = (
  searchQuery: string,
  signal: GenericAbortSignal
): Promise<OpportunityShareSearchResult[]> =>
  axiosGet({ path: `${apiRouteUtils.opportunitySearch.url}?query=${searchQuery}`, signal })

export const shareOpportunities = async (params: ShareOpportunityParams): Promise<number> => {
  const requestParams = { path: apiRouteUtils.opportunityShare.url, data: params }
  return await axiosPost<number>(requestParams)
    .then((data: number) => data)
    .catch((error: Error) => {
      throw error
    })
}
export const clearSfCache = (): Promise<void> =>
  axiosPost({ path: apiRouteUtils.salesforceClearCache.url })
    .then(() => console.log('Salesforce cache cleared'))
    .catch((error: Error) => {
      throw error
    })

export const updateActiveSession = (userId: string, payload: Record<string, any>): Promise<Record<string, any>> =>
  axiosPost({
    path: `${apiRouteUtils.activeSession.url}?userId=${userId}`,
    contentType: 'application/json',
    data: payload
  })

export const updateUserSettings = (language: string): Promise<UserSettings> =>
  axiosPost({ path: apiRouteUtils.userSettings.url, contentType: 'application/json', data: { language } })

export const saveOffer = (offer: Offer, tabId: string = getUITabId()): Promise<SavedOffer> =>
  axiosPost<{ data: SavedOffer }>({
    path: `${apiRouteUtils.saveOffer.url}?opportunityId=${offer.opportunityId}`,
    contentType: apiRouteUtils.saveOffer.contentType,
    data: { ...offer, tabId },
    timeout: apiRouteUtils.saveOffer.timeout
  }).then(response => response.data)

export const saveOfferFromContract = (offer: Offer, tabId: string = getUITabId()): Promise<SavedOffer> =>
  axiosPost<{ data: SavedOffer }>({
    path: `${apiRouteUtils.saveOffer.url}?opportunityId=${offer.opportunityId}&isUpdatedFromContract=true`,
    contentType: apiRouteUtils.saveOffer.contentType,
    data: { ...offer, tabId },
    timeout: apiRouteUtils.saveOffer.timeout
  }).then(response => response.data)

export const equipmentCreationSAP = (data: any): Promise<any> =>
  axiosPost({
    path: apiRouteUtils.equipmentCreation.url,
    contentType: 'application/json',
    data
  })
export const getContractPDF = (
  offerId: OfferDbId,
  language: string,
  documentGenerator: DocumentGenerator,
  documentTemplate: string,
  tabId: string = getUITabId()
): Promise<void> =>
  axiosPost({
    path: `${apiRouteUtils.gendocGetContractPDF.url}/${offerId}`,
    contentType: apiRouteUtils.gendocGetContractPDF.contentType,
    data: {
      documentGenerator,
      documentTemplate,
      language,
      tabId
    } as GetContractPdfRequest
  })

export const createGendocXml = (offerId: string, language: string, documentTemplate: string): Promise<string> =>
  axiosPost({
    path: `${apiRouteUtils.createGendocXml.url}/${offerId}`,
    contentType: apiRouteUtils.createGendocXml.contentType,
    data: {
      documentTemplate,
      language
    } as CreateGendocXmlRequest
  })

export const recreateDocuments = (
  offerId: OfferDbId,
  enabledDocumentTemplates: DocumentTemplate[],
  tabId: string = getUITabId()
): Promise<void> =>
  axiosPost({
    path: `${apiRouteUtils.recreateGendocDocuments.url}/${offerId}`,
    contentType: apiRouteUtils.recreateGendocDocuments.contentType,
    data: { enabledDocumentTemplates, tabId } as RecreateDocumentsRequest
  })

export const sendContractPdf = (
  offerId: OfferDbId,
  to: string,
  ccTo: string,
  contractFilenames: ContractFilenames,
  language: string,
  documentGenerator: DocumentGenerator,
  documentTemplate: string,
  tabId: string = getUITabId()
) =>
  axiosPost({
    path: `${apiRouteUtils.sendContractPdf.url}/${offerId}`,
    contentType: apiRouteUtils.sendContractPdf.contentType,
    data: {
      to,
      ccTo,
      contractFilenames,
      language,
      documentGenerator,
      documentTemplate,
      tabId
    } as SendContractPdfRequest
  })
export const activateVersion = (offerversionId: OfferDbId, tabId: string = getUITabId()): Promise<SavedOffer> =>
  axiosPost<{ data: SavedOffer }>({
    path: apiRouteUtils.activateVersion.url,
    contentType: apiRouteUtils.saveOffer.contentType,
    data: { tabId, offerversionId }
  }).then(response => response.data)

export const finalizeOffer = (
  offerData: Offer,
  opportunityId: OpportunitySfId,
  tabId: string = getUITabId()
): Promise<SavedOffer> =>
  axiosPost<{ data: SavedOffer }>({
    path: apiRouteUtils.finalizeOffer.url,
    contentType: apiRouteUtils.finalizeOffer.contentType,
    data: { ...offerData, opportunityId, tabId }
  }).then(response => response.data)

export const getDiscountApprovalStatus = (
  offerId: OfferDbId,
  tenderVersionId: string
): Promise<UpdateDiscountApprovalStatusResponse> =>
  axiosPost<{ data: UpdateDiscountApprovalStatusResponse }>({
    path: apiRouteUtils.discountApprovalStatus.url,
    contentType: apiRouteUtils.discountApprovalStatus.contentType,
    data: { offerId, tenderVersionId }
  }).then(response => response.data)
